import React, { useId } from "react";
import { List, ListItem, ListItemText } from "@mui/material";
import { Card } from "@outpave/ds";

interface SettingsCardProps {
  title: string;
  subtitle?: string;
  /** Top-right of the card, beside the title. */
  action?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * A titled settings card whose rows read a name and description on the left
 * and their control on the right.
 */
export const SettingsCard: React.FC<SettingsCardProps> = ({
  title,
  subtitle,
  action,
  children,
}) => (
  <Card
    variant="outlined"
    title={title}
    subtitle={subtitle}
    action={action ? { type: "content", content: action } : undefined}
  >
    {children}
  </Card>
);

/** Rows of one settings section, divided by hairlines. */
export const SettingRows: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <List disablePadding>{children}</List>;

interface SettingRowProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Gets the id of the row's title, to name the control it renders. */
  control: (labelId: string) => React.ReactNode;
  divider?: boolean;
}

export const SettingRow: React.FC<SettingRowProps> = ({
  title,
  description,
  control,
  divider = true,
}) => {
  const labelId = useId();

  return (
    <ListItem disableGutters divider={divider} sx={{ gap: "var(--space-4)" }}>
      <ListItemText
        primary={title}
        secondary={description}
        slotProps={{ primary: { id: labelId } }}
      />
      {control(labelId)}
    </ListItem>
  );
};
